<template>
  <div class="mypage">
    <div class="container">
      <h2>{{ $t("myPage.title") }}</h2>
      <div>
        <!-- Fish情報 -->
        <div class="fish-info">
          <!-- エラー状態 -->
          <div v-if="fishError" class="fish-error">
            <p class="error-message">{{ fishError }}</p>
            <button class="retry-button" @click="retryFetchFishData">{{ $t("myPage.fish.retry") }}</button>
          </div>

          <template v-else>
            <!-- プレミアムアイコン -->
            <div v-if="!isFreePlan" class="premium-badge">
              <img src="@/assets/img/icon/premium.png" width="24" height="24" alt="Premium" />
            </div>
            <div class="fish-info-title">{{ $t("myPage.fish.holdings") }}</div>
            <div class="fish-info-data">
              <div class="fish-info-data-title">
                <img
                  class="fish-info-data-title-icon"
                  src="@/assets/img/icon/fish_icon.png"
                  :alt="$t('myPage.fish.iconAlt').toString()"
                />
                <span v-if="fishLoading" class="skeleton-text skeleton-amount"></span>
                <template v-else>{{ formattedCurrentFish }}</template>
              </div>
              <div class="fish-info-data-description">
                <div class="fish-info-data-description-item">
                  <div class="fish-category">{{ $t("myPage.fish.charged") }}</div>
                  <div class="fish-value">
                    <span v-if="fishLoading" class="skeleton-text skeleton-value"></span>
                    <template v-else>{{ formattedPrepaidFish }}</template>
                  </div>
                </div>
                <div class="fish-info-data-description-item-plus"><PlusIcon></PlusIcon></div>
                <div class="fish-info-data-description-item">
                  <div class="fish-category">{{ $t("myPage.fish.bonus") }}</div>
                  <div class="fish-value">
                    <span v-if="fishLoading" class="skeleton-text skeleton-value"></span>
                    <template v-else>{{ formattedFreeFish }}</template>
                  </div>
                </div>
              </div>
            </div>
            <div class="fish-info-action">
              <div class="fish-info-action-buttons">
                <button class="fish-purchase-button" @click="onClickPurchase">
                  <PlusIcon></PlusIcon>{{ $t("myPage.fish.chargeFish") }}
                </button>
                <button class="upgrade-button" @click="onClickUpgrade" v-if="isFreePlan && !hasPaymentError">
                  <img src="@/assets/img/icon/premium.png" width="24" height="24" />{{ $t("myPage.fish.upgrade") }}
                </button>
              </div>
              <div class="fish-info-links">
                <router-link to="/fish-history" class="fish-link">{{ $t("myPage.fish.usageHistory") }}</router-link>
                <router-link class="fish-link" :to="{ name: 'paymentManagement' }">{{
                  $t("myPage.fish.paymentManagement")
                }}</router-link>
              </div>
            </div>
          </template>
        </div>
      </div>
      <payment-description />
      <my-page-user-info-section />

      <!--
        商業化希望トグル
        NOTE: 企画マッチング FAB の表示ゲートには使わない。
        後続の応募レコメンド等（一定文字数到達時など）の切り分け用（設計書 §10.2）。
      -->
      <!-- <div class="commercialization-section">
        <h2 class="h2">{{ $t("myPage.commercialization.title") }}</h2>
        <label class="commercialization-toggle">
          <input
            type="checkbox"
            :checked="commercializationEnabled"
            :disabled="commercializationSaving"
            @change="onToggleCommercialization"
          />
          <span>{{ $t("myPage.commercialization.label") }}</span>
        </label>
        <p class="comment">{{ $t("myPage.commercialization.description") }}</p>
      </div> -->

      <!-- アシスタント猫セクション（作家タイプ診断済み） -->
      <div v-if="hasWriterTypeResult" class="assistant-cat-section">
        <h2 class="h2">
          <cat-icon size="small" :clickable="false" :writer-type="selectedCatType" class="section-icon" />
          {{ $t("myPage.assistantCat.title") }}
        </h2>
        <button class="assistant-cat-button" @click="onClickChangeAssistantCat">
          {{ $t("myPage.assistantCat.changeButton") }}
        </button>
        <p class="title">{{ $t("myPage.assistantCat.nicknameLabel") }}</p>
        <aside>{{ selectedCatNickname }}</aside>
        <p class="comment">{{ $t("myPage.assistantCat.nicknameDescription") }}</p>
        <button class="assistant-cat-button" @click="onClickChangeNickname">
          {{ $t("myPage.assistantCat.changeNicknameButton") }}
        </button>
        <p>{{ $t("myPage.assistantCat.modeLabel") }}</p>
        <aside class="mode-title">
          {{ feedbackManuscriptMode }}
          <button class="mode-change-button" type="button" @click="onClickChangeFeedbackManuscriptMode">
            {{ $t("myPage.assistantCat.changeModeButton") }}
          </button>
        </aside>
        <p>{{ $t("myPage.assistantCat.summaryModeLabel") }}</p>
        <aside class="mode-title">
          {{ generateSummaryMode }}
          <button class="mode-change-button" type="button" @click="onClickChangeGenerateSummaryMode">
            {{ $t("myPage.assistantCat.changeModeButton") }}
          </button>
        </aside>
      </div>

      <!-- アシスタント猫セクション（作家タイプ診断未実施） -->
      <div v-else class="assistant-cat-section">
        <h2 class="h2">{{ $t("myPage.assistantCat.noResult.title") }}</h2>
        <p>{{ $t("myPage.assistantCat.noResult.description") }}</p>
        <div class="no-result-btn-wrapper">
          <button class="assistant-cat-button" @click="onClickWriterTypeIndicator">
            {{ $t("myPage.assistantCat.noResult.takeTestButton") }}
          </button>
          <button class="assistant-cat-button btn-skip" @click="onClickSkipWriterType">
            {{ $t("myPage.assistantCat.noResult.skipButton") }}
          </button>
        </div>
      </div>

      <!-- アシスタント猫選択ダイアログ -->
      <AssistantCatSelectionDialog
        v-if="showAssistantCatDialog"
        :assistant-cat-master="assistantCatMaster"
        @close="onCloseAssistantCatDialog"
      />

      <!-- ニックネーム変更ダイアログ群 -->
      <CatNicknameInputDialog
        v-if="showNicknameInputDialog"
        :assistant-cat="selectedAssistantCat"
        :initial-nickname="inputDialogInitialNickname"
        @save="onSaveNickname"
        @cancel="onCancelNicknameInput"
      />

      <CatNicknameConfirmDialog
        v-if="showNicknameConfirmDialog"
        :assistant-cat="selectedAssistantCat"
        :nickname="pendingNickname"
        :is-resetting-to-default="isResettingNicknameToDefault"
        @confirm="onConfirmNickname"
        @close="onCloseNicknameConfirm"
      />

      <CatNicknameSuccessDialog
        v-if="showNicknameSuccessDialog"
        :assistant-cat="selectedAssistantCat"
        :reaction-message="nicknameReactionMessage"
        @ok="onOkNicknameSuccess"
      />

      <!-- 作家タイプ診断スキップ確認ダイアログ -->
      <WriterTypeIndicatorSkipConfirmDialog
        v-if="showSkipConfirmDialog"
        @close="showSkipConfirmDialog = false"
        @done="onSkipConfirmDone"
      />

      <!-- 感想モード選択ダイアログ -->
      <FeedbackManuscriptModeSelectionDialog
        v-if="showFeedbackManuscriptModeDialog"
        :assistant-cat-master="assistantCatMaster"
        @close="onCloseFeedbackManuscriptModeDialog"
      />

      <!-- あらすじモード選択ダイアログ -->
      <GenerateSummaryModeSelectionDialog
        v-if="showGenerateSummaryModeDialog"
        :assistant-cat-master="assistantCatMaster"
        @close="onCloseGenerateSummaryModeDialog"
      />

      <!-- テーマ設定セクション -->
      <div class="theme-settings-section">
        <h2 class="h2">{{ $t("appTheme.label") }}</h2>
        <p>{{ $t("myPage.themeSettings.description") }}</p>
        <p class="trial-notice">{{ $t("myPage.themeSettings.trialNotice") }}</p>
        <div class="theme-dropdown-container">
          <select class="theme-dropdown" :value="appTheme" @change="onChangeTheme($event)">
            <option value="light">{{ $t("appTheme.light") }}</option>
            <option value="dark">{{ $t("appTheme.dark") }}</option>
            <option value="system">{{ $t("appTheme.system") }}</option>
          </select>
        </div>

        <p class="language-label">{{ $t("myPage.languageSettings.title") }}</p>
        <div class="language-dropdown-container">
          <select class="theme-dropdown" :value="selectedLocale" @change="onChangeLocale($event)">
            <option v-for="option in localeOptions" :key="option.code" :value="option.code">{{ option.label }}</option>
          </select>
        </div>
      </div>

      <h2 class="h2">{{ $t("myPage.passwordChange.title") }}</h2>
      <p>{{ $t("myPage.passwordChange.description") }}</p>
      <router-link class="button cancel" :to="{ name: 'changePassword' }">{{
        $t("myPage.passwordChange.button")
      }}</router-link>
      <h2 class="h2">{{ $t("myPage.emailChange.title") }}</h2>
      <p>{{ $t("myPage.emailChange.description") }}</p>
      <router-link class="button cancel" :to="{ name: 'changeEmail' }">{{
        $t("myPage.emailChange.button")
      }}</router-link>
      <div class="email-notification-section">
        <h2 class="h2">{{ $t("myPage.emailNotification.title") }}</h2>
        <div class="email-magazine-row">
          <div class="email-magazine-content">
            <p class="email-magazine-title">{{ $t("myPage.emailNotification.magazine.title") }}</p>
            <p class="email-magazine-description">{{ $t("myPage.emailNotification.magazine.description") }}</p>
            <ul class="email-magazine-list">
              <li>{{ $t("myPage.emailNotification.magazine.item1") }}</li>
              <li>{{ $t("myPage.emailNotification.magazine.item2") }}</li>
              <li>{{ $t("myPage.emailNotification.magazine.item3") }}</li>
            </ul>
          </div>
          <div class="email-magazine-options-wrapper">
            <div
              class="email-magazine-options"
              :class="{ disabled: emailNotificationLoading || emailNotificationUpdating }"
            >
              <button
                type="button"
                class="email-magazine-option"
                :class="{ 'is-disabled': currentLocale !== 'ja' }"
                :disabled="currentLocale !== 'ja' || emailNotificationLoading || emailNotificationUpdating"
                @click="onChangeEmailMagazine(true)"
              >
                <radio-icon
                  value="receive"
                  :selected="emailMagazineEnabled ? 'receive' : 'notReceive'"
                  :fill="emailMagazineEnabled ? 'var(--color-primary)' : 'var(--color-inactive)'"
                />
                <span>{{ $t("myPage.emailNotification.receive") }}</span>
              </button>
              <button
                type="button"
                class="email-magazine-option"
                :disabled="emailNotificationLoading || emailNotificationUpdating"
                @click="onChangeEmailMagazine(false)"
              >
                <radio-icon
                  value="notReceive"
                  :selected="emailMagazineEnabled ? 'receive' : 'notReceive'"
                  :fill="!emailMagazineEnabled ? 'var(--color-primary)' : 'var(--color-inactive)'"
                />
                <span>{{ $t("myPage.emailNotification.notReceive") }}</span>
              </button>
            </div>
            <p v-if="currentLocale !== 'ja'" class="email-magazine-japanese-notice">
              {{ $t("myPage.emailNotification.japaneseOnlyNotice") }}
            </p>
          </div>
        </div>
      </div>
      <h2 class="h2">{{ $t("myPage.logout.title") }}</h2>
      <p>{{ $t("myPage.logout.description") }}</p>
      <button class="button cancel" @click="logout">{{ $t("myPage.logout.button") }}</button>
      <div v-if="!isSchoolPlan">
        <h2 class="h2">{{ $t("myPage.accountDeletion.title") }}</h2>
        <p>
          {{ $t("myPage.accountDeletion.description") }}<br />
          <span style="color: var(--color-warning)">{{ $t("myPage.accountDeletion.warning") }}</span>
        </p>
        <button
          class="button danger"
          :class="{ processing: isProcessing }"
          :disabled="isProcessing"
          @click="onClickDeleteAccount"
        >
          {{ $t("myPage.accountDeletion.button") }}
        </button>
      </div>
    </div>
    <site-footer class="footer"></site-footer>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import PaymentDescription from "@/components/organisms/subscription/PaymentDescription.vue";
import MyPageUserInfoSection from "@/components/organisms/MyPageUserInfoSection.vue";
import SiteFooter from "@/components/organisms/Footer.vue";
import CatIcon from "@/components/atoms/CatIcon.vue";

import { Auth } from "aws-amplify";
import { Dialog } from "@/lib/utils";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/SimpleDialog.vue";
import UserDeleteDialog, { UserDeleteDialogProps } from "@/components/ui/UserDeleteDialog.vue";
import AssistantCatSelectionDialog from "@/components/ui/AssistantCatSelectionDialog.vue";
import CatNicknameInputDialog from "@/components/ui/CatNicknameInputDialog.vue";
import CatNicknameConfirmDialog from "@/components/ui/CatNicknameConfirmDialog.vue";
import CatNicknameSuccessDialog from "@/components/ui/CatNicknameSuccessDialog.vue";
import FeedbackManuscriptModeSelectionDialog from "@/components/ui/FeedbackManuscriptModeSelectionDialog.vue";
import GenerateSummaryModeSelectionDialog from "@/components/ui/GenerateSummaryModeSelectionDialog.vue";
import WriterTypeIndicatorSkipConfirmDialog from "@/components/ui/WriterTypeIndicatorSkipConfirmDialog.vue";
import { UserClient, UserEmailClient } from "@/lib/clients";
import { User, Plan, FeedbackManuscriptMode, GenerateSummaryMode } from "@/lib/models";
import { isReceiveAllEnabled, toReceiveAggregation } from "@/lib/utils/userEmailSettings";
import { AssistantCatMaster, AssistantCat } from "@/lib/models/assistantCat";
import { db } from "@/lib/indexeddb";
import { logout as performLogout } from "@/lib/utils/auth";
import { setUserLocale, getCurrentMode, LOCALE_LABELS } from "@/lib/utils/i18n/locale";
import i18n from "@/lib/i18n";
import { mapGetters, mapActions } from "vuex";
import PlusIcon from "icons/Plus.vue";
import RadioIcon from "@/components/atoms/RadioIcon.vue";
import { NolaAnalytics, NolaItemId } from "@/lib/utils/analytics";
import { AppTheme } from "@/store/modules/general";

const MODE_TITLE_KEYS: Record<FeedbackManuscriptMode, string> = {
  [FeedbackManuscriptMode.NORMAL]: "myPage.assistantCat.modeNames.normal",
  [FeedbackManuscriptMode.DRY]: "myPage.assistantCat.modeNames.dry",
  [FeedbackManuscriptMode.SWEET]: "myPage.assistantCat.modeNames.sweet",
};

const SUMMARY_MODE_TITLE_KEYS: Record<GenerateSummaryMode, string> = {
  [GenerateSummaryMode.WAKUWAKU]: "myPage.assistantCat.summaryModeNames.wakuwaku",
  [GenerateSummaryMode.FUMUFUMU]: "myPage.assistantCat.summaryModeNames.fumufumu",
};

const userClient = new UserClient();
const userEmailClient = new UserEmailClient();

export default Vue.extend<Data, Methods, Computed, {}>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    PaymentDescription,
    MyPageUserInfoSection,
    SiteFooter,
    PlusIcon,
    RadioIcon,
    CatIcon,
    AssistantCatSelectionDialog,
    CatNicknameInputDialog,
    CatNicknameConfirmDialog,
    CatNicknameSuccessDialog,
    FeedbackManuscriptModeSelectionDialog,
    GenerateSummaryModeSelectionDialog,
    WriterTypeIndicatorSkipConfirmDialog,
  },
  data() {
    return {
      email: "",
      isProcessing: false,
      showAssistantCatDialog: false,
      showFeedbackManuscriptModeDialog: false,
      showGenerateSummaryModeDialog: false,
      showSkipConfirmDialog: false,
      assistantCatMaster: null as AssistantCatMaster | null,
      assistantCats: [] as AssistantCat[],
      // ニックネーム変更ダイアログの状態管理
      showNicknameInputDialog: false,
      showNicknameConfirmDialog: false,
      showNicknameSuccessDialog: false,
      pendingNickname: "",
      nicknameReactionMessage: "",
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      selectedLocale: getCurrentMode(),
      emailMagazineEnabled: false,
      emailNotificationLoading: false,
      emailNotificationUpdating: false,
      commercializationEnabled: false,
      commercializationSaving: false,
    };
  },
  computed: {
    ...mapGetters("fishModule", [
      "formattedCurrentFish",
      "formattedPrepaidFish",
      "formattedFreeFish",
      "isLoading",
      "error",
    ]),
    ...mapGetters("billingModule", ["hasPaymentError"]),
    wantsCommercialization(): boolean {
      return this.user?.wantsCommercialization === true;
    },
    user() {
      return this.$store.getters["userModule/user"] as User;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFreePlan() {
      return this.plan === Plan.free;
    },
    isSchoolPlan() {
      return this.plan === Plan.school;
    },
    fishLoading() {
      return this.$store.getters["fishModule/isLoading"];
    },
    fishError() {
      return this.$store.getters["fishModule/error"];
    },
    hasWriterTypeResult() {
      // assistantCatMasterがnullでない場合は作家タイプ診断済み
      return this.assistantCatMaster !== null;
    },
    selectedCatType() {
      return this.assistantCatMaster?.selectedType || null;
    },
    selectedCatNickname() {
      const selectedCat = this.assistantCats.find((cat: AssistantCat) => cat.selected);
      return selectedCat?.name || "";
    },
    selectedCatAssistantType() {
      const selectedCat = this.assistantCats.find((cat: AssistantCat) => cat.selected);
      return selectedCat?.assistantCatType || null;
    },
    selectedAssistantCat() {
      return this.assistantCats.find((cat: AssistantCat) => cat.selected) || null;
    },
    feedbackManuscriptMode(): string {
      const mode = this.assistantCatMaster?.feedbackManuscriptMode ?? FeedbackManuscriptMode.NORMAL;
      const key = MODE_TITLE_KEYS[mode];
      return this.$t(key) as string;
    },
    generateSummaryMode(): string {
      const mode = this.assistantCatMaster?.generateSummaryMode ?? GenerateSummaryMode.WAKUWAKU;
      const key = SUMMARY_MODE_TITLE_KEYS[mode];
      return this.$t(key) as string;
    },
    inputDialogInitialNickname() {
      // キャンセルで戻った場合はpendingNickname、初回表示時はselectedCatNickname
      return this.pendingNickname || this.selectedCatNickname;
    },
    // 現在の UI 表示言語。将来対応言語が増えた場合に判定式を変えやすくするため、
    // ロケール文字列のまま保持する。
    currentLocale() {
      return this.$i18n.locale;
    },
    isResettingNicknameToDefault() {
      return this.pendingNickname === "";
    },
    appTheme() {
      return this.$store.getters["generalModule/appTheme"];
    },
    localeOptions() {
      return [
        { code: "system", label: this.$t("myPage.languageSettings.system") as string },
        ...Object.entries(LOCALE_LABELS)
          .filter(([code]) => code !== "system")
          .map(([code, label]) => ({ code, label })),
      ];
    },
  },
  watch: {
    wantsCommercialization: {
      immediate: true,
      handler(enabled: boolean) {
        // 保存中はローカル操作を優先し、store 同期で上書きしない
        if (!this.commercializationSaving) {
          this.commercializationEnabled = enabled;
        }
      },
    },
  },
  methods: {
    ...mapActions("writerTypeResultModule", ["fetchMyResult"]),
    onChangeTheme(event: Event) {
      const target = event.target as HTMLSelectElement;
      const theme = target.value as AppTheme;
      this.$store.dispatch("generalModule/setAppTheme", theme);
    },
    onChangeLocale(event: Event) {
      const target = event.target as HTMLSelectElement;
      setUserLocale(target.value);
      this.selectedLocale = getCurrentMode();
      const { title } = this.$route.meta;
      if (title && typeof title === "string" && title.includes(".")) {
        document.title = `Nola ${i18n.t(title)}`;
      }
    },
    async logout() {
      await performLogout();
    },
    async onToggleCommercialization(event: Event) {
      if (this.commercializationSaving) return;

      const target = event.target as HTMLInputElement;
      const { checked } = target;
      const previous = this.commercializationEnabled;
      this.commercializationEnabled = checked;
      this.commercializationSaving = true;
      try {
        await this.$store.dispatch("userModule/updateUser", {
          wantsCommercialization: checked,
        });
      } catch (error) {
        this.commercializationEnabled = previous;
        const warningDialog = new Dialog(SimpleDialog);
        const options: SimpleDialogProps = {
          title: this.$t("myPage.commercialization.title") as string,
          content: this.$t("myPage.commercialization.saveError") as string,
        };
        warningDialog.show(options);
      } finally {
        this.commercializationSaving = false;
      }
    },
    async onClickDeleteAccount() {
      this.isProcessing = true;

      const results = await userClient.checkConnectedOtherServices();

      this.isProcessing = false;

      if (results.nolaPublishing.error || results.nolaNovel.error) {
        let errorMessage = "";
        if (results.nolaNovel.error) {
          errorMessage += `${this.$t("myPage.deleteDialog.errorLabelNolaNovel")}${results.nolaNovel.error}`;
        }
        if (results.nolaPublishing.error) {
          errorMessage += errorMessage && "\n";
          errorMessage += `${this.$t("myPage.deleteDialog.errorLabelNolaPublishing")}${results.nolaPublishing.error}`;
        }
        const warningDialog = new Dialog(SimpleDialog);
        const options: SimpleDialogProps = {
          title: this.$t("myPage.deleteDialog.cannotDeleteTitle") as string,
          content: `${this.$t("myPage.deleteDialog.cannotDeleteMessage")}${errorMessage}`,
        };
        warningDialog.show(options);
        return;
      }

      const confirmDialog = new Dialog(UserDeleteDialog);
      const data: UserDeleteDialogProps = {
        existsNolaPublishingUser: results.nolaPublishing.result!,
        existsNolaNovelUser: results.nolaNovel.result!,
        positive: this.$t("common.delete") as string,
        positiveCallback: async () => this.deleteUser(),
      };
      confirmDialog.show(data);
    },
    async deleteUser() {
      this.$store.dispatch("userModule/deleteUser", {
        email: this.email,
        callback: async () => {
          await Auth.signOut();
          window.localStorage.clear();
          window.sessionStorage.removeItem("vuex"); // VuexのStoreを初期化
          db.delete(); // IndexedDBの初期化
          // 初期化したDBへの次の接続を担保するためにリロードを走らせる
          window.location.href = "/accountDeleted";
        },
      });
    },
    async onClickPurchase() {
      // PurchaseFishページに遷移する
      this.$router.push({ name: "purchaseFish" });
    },
    async onClickUpgrade() {
      // プレミアム登録ページに遷移する
      this.$router.push({ name: "subscriptionAnnounce" });
    },
    async retryFetchFishData() {
      await this.$store.dispatch("fishModule/fetchFishData");
    },
    onClickChangeAssistantCat() {
      this.showAssistantCatDialog = true;
    },
    onClickChangeFeedbackManuscriptMode() {
      this.showFeedbackManuscriptModeDialog = true;
    },
    onClickChangeGenerateSummaryMode() {
      this.showGenerateSummaryModeDialog = true;
    },
    async onCloseAssistantCatDialog() {
      this.showAssistantCatDialog = false;
      // ダイアログ閉じた後にアシスタント猫データを再取得（変更が反映される）
      if (this.hasWriterTypeResult) {
        await this.fetchAssistantCats();
        // CatIconに反映するためにassistantCatMasterも更新
        await this.fetchAssistantCatMaster();
      }
    },
    async onCloseFeedbackManuscriptModeDialog() {
      this.showFeedbackManuscriptModeDialog = false;
      await this.fetchAssistantCatMaster();
    },
    async onCloseGenerateSummaryModeDialog() {
      this.showGenerateSummaryModeDialog = false;
      await this.fetchAssistantCatMaster();
    },
    onClickChangeNickname() {
      // 入力ダイアログを開く際にpendingNicknameをリセット
      this.pendingNickname = "";
      this.showNicknameInputDialog = true;
    },
    onSaveNickname(nickname: string) {
      this.pendingNickname = nickname;

      this.showNicknameInputDialog = false;
      this.showNicknameConfirmDialog = true;
    },
    onCancelNicknameInput() {
      this.showNicknameInputDialog = false;
    },
    async onConfirmNickname() {
      if (!this.selectedCatAssistantType) {
        return;
      }

      try {
        await this.$store.dispatch("assistantCatModule/updateCatNickname", {
          assistantCatType: this.selectedCatAssistantType,
          name: this.pendingNickname,
        });

        // 成功時はアシスタント猫一覧を再取得して最新データを反映
        await this.fetchAssistantCats();

        // GAイベントを発火
        this.nolaAnalytics.logButtonEvent(NolaItemId.AssistantCatChangeNickname, this.selectedCatAssistantType);

        // リアクションメッセージを設定（再取得後のselected: trueの猫のreactionAfterSetNameを使用）
        this.nicknameReactionMessage = this.selectedAssistantCat?.reactionAfterSetName || "";

        // 確認ダイアログを閉じて成功ダイアログを表示
        this.showNicknameConfirmDialog = false;
        this.showNicknameSuccessDialog = true;
      } catch (error) {
        console.error("Failed to change nickname:", error);
        this.showNicknameSuccessDialog = false;
        this.showNicknameConfirmDialog = true;
        this.$notify({
          title: this.$t("myPage.assistantCat.nicknameChangeError") as string,
          type: "error",
        });
      }
    },
    onCloseNicknameConfirm() {
      this.showNicknameConfirmDialog = false;
    },
    onOkNicknameSuccess() {
      this.showNicknameSuccessDialog = false;
      this.pendingNickname = "";
      this.nicknameReactionMessage = "";
    },
    onClickWriterTypeIndicator() {
      const baseUrl = process.env.VUE_APP_NOLA_WEB;
      const writerTypeUrl = `${baseUrl}/writer-type-indicator/`;
      window.open(writerTypeUrl, "_blank");
    },
    onClickSkipWriterType() {
      this.showSkipConfirmDialog = true;
    },
    async onSkipConfirmDone() {
      this.showSkipConfirmDialog = false;
      await this.fetchAssistantCatMaster();
      await this.fetchAssistantCats();
    },
    async fetchAssistantCatMaster() {
      try {
        this.assistantCatMaster = await this.$store.dispatch("assistantCatModule/fetchAssistantCatMaster");
      } catch (error) {
        console.error("Failed to fetch assistant cat master:", error);
        // エラー時はnullのままにして、作家タイプ診断未受診として扱う
        this.assistantCatMaster = null;
      }
    },
    async fetchAssistantCats() {
      try {
        this.assistantCats = await this.$store.dispatch("assistantCatModule/fetchAssistantCatList");
      } catch (error) {
        console.error("Failed to fetch assistant cat list:", error);
        // エラー時は空配列のままにする
        this.assistantCats = [];
      }
    },
    async fetchEmailNotificationSettings() {
      this.emailNotificationLoading = true;
      try {
        const settings = await userEmailClient.fetchEmailReceiveSettings();
        this.emailMagazineEnabled = isReceiveAllEnabled(settings.receiveAggregation);
      } catch (error) {
        console.error("Failed to fetch email notification settings:", error);
        this.$notify({
          title: this.$t("myPage.emailNotification.loadError") as string,
          type: "error",
        });
      } finally {
        this.emailNotificationLoading = false;
      }
    },
    async onChangeEmailMagazine(enabled: boolean) {
      if (this.emailNotificationLoading || this.emailNotificationUpdating) {
        return;
      }
      // GDPR対策: 日本語以外のロケールでは「希望する」への変更を受け付けない
      if (enabled && this.currentLocale !== "ja") {
        return;
      }
      if (enabled === this.emailMagazineEnabled) {
        return;
      }

      const previous = this.emailMagazineEnabled;
      this.emailMagazineEnabled = enabled;
      this.emailNotificationUpdating = true;

      try {
        await userEmailClient.updateEmailReceiveSettings(toReceiveAggregation(enabled));
      } catch (error) {
        console.error("Failed to update email notification settings:", error);
        this.emailMagazineEnabled = previous;
        this.$notify({
          title: this.$t("myPage.emailNotification.updateError") as string,
          type: "error",
        });
      } finally {
        this.emailNotificationUpdating = false;
      }
    },
  },
  async created() {
    const user = await Auth.currentUserPoolUser().catch((err) => null);
    if (user) {
      const email = user.attributes.email || user.username;
      this.email = email;
    }

    await Promise.all([
      this.$store.dispatch("userModule/initialize"),
      this.$store.dispatch("billingModule/fetchBillingInfo"),
      this.fetchMyResult(), // 作家タイプ診断の結果を取得
      this.fetchAssistantCatMaster(), // アシスタント猫マスタの取得
    ]);

    // 作家タイプ診断済みの場合のみアシスタント猫一覧を取得
    if (this.hasWriterTypeResult) {
      await this.fetchAssistantCats();
    }

    // user クエリで user_email 同期が走った後に設定を取得する
    await userClient.fetchUser();
    await this.fetchEmailNotificationSettings();
  },
  mounted() {
    this.$store.dispatch("fishModule/fetchFishData");
  },
});

interface Data {
  email: string;
  isProcessing: boolean;
  showAssistantCatDialog: boolean;
  showFeedbackManuscriptModeDialog: boolean;
  showGenerateSummaryModeDialog: boolean;
  showSkipConfirmDialog: boolean;
  assistantCatMaster: AssistantCatMaster | null;
  assistantCats: AssistantCat[];
  showNicknameInputDialog: boolean;
  showNicknameConfirmDialog: boolean;
  showNicknameSuccessDialog: boolean;
  pendingNickname: string;
  nicknameReactionMessage: string;
  nolaAnalytics: NolaAnalytics;
  selectedLocale: string;
  emailMagazineEnabled: boolean;
  emailNotificationLoading: boolean;
  emailNotificationUpdating: boolean;
  commercializationEnabled: boolean;
  commercializationSaving: boolean;
}

interface Computed {
  user: User;
  wantsCommercialization: boolean;
  plan: Plan;
  isFreePlan: boolean;
  isSchoolPlan: boolean;
  formattedCurrentFish: string;
  formattedPrepaidFish: string;
  formattedFreeFish: string;
  fishLoading: boolean;
  fishError: string | null;
  hasPaymentError: boolean;
  hasWriterTypeResult: boolean;
  selectedCatType: string | null;
  selectedCatNickname: string;
  selectedCatAssistantType: string | null;
  selectedAssistantCat: AssistantCat | null;
  inputDialogInitialNickname: string;
  currentLocale: string;
  isResettingNicknameToDefault: boolean;
  appTheme: AppTheme;
  localeOptions: Array<{ code: string; label: string }>;
  feedbackManuscriptMode: string;
  generateSummaryMode: string;
}

interface Methods {
  logout: () => void;
  onToggleCommercialization: (event: Event) => Promise<void>;
  onClickDeleteAccount: () => void;
  deleteUser: () => void;
  onClickPurchase: () => void;
  onClickUpgrade: () => void;
  retryFetchFishData: () => void;
  onClickChangeAssistantCat: () => void;
  onClickChangeFeedbackManuscriptMode: () => void;
  onClickChangeGenerateSummaryMode: () => void;
  onCloseAssistantCatDialog: () => void;
  onCloseFeedbackManuscriptModeDialog: () => void;
  onCloseGenerateSummaryModeDialog: () => Promise<void>;
  onClickChangeNickname: () => void;
  onSaveNickname: (nickname: string) => void;
  onCancelNicknameInput: () => void;
  onConfirmNickname: () => Promise<void>;
  onCloseNicknameConfirm: () => void;
  onOkNicknameSuccess: () => void;
  onClickWriterTypeIndicator: () => void;
  onClickSkipWriterType: () => void;
  onSkipConfirmDone: () => Promise<void>;
  fetchMyResult: () => Promise<void>;
  fetchAssistantCatMaster: () => Promise<void>;
  fetchAssistantCats: () => Promise<void>;
  fetchEmailNotificationSettings: () => Promise<void>;
  onChangeEmailMagazine: (enabled: boolean) => Promise<void>;
  onChangeTheme: (event: Event) => void;
  onChangeLocale: (event: Event) => void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mypage {
  background-color: var(--color-background-normal);
  min-height: 100%;
  position: relative;
}

.container {
  padding: 20px;
  max-width: 750px;
  margin: 0 auto;
}

.pen-name-field {
  --text-field-bg: var(--color-surface-base);
  --text-field-border: none;
}

.title {
  margin: 10px 7px;
}
aside {
  margin: 10px 7px;
  padding: 15px;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-xs);
  color: var(--color-text-normal);
}
.comment {
  margin: 0px 7px;
  color: var(--color-text-low);
}

.commercialization-section {
  margin: 24px 7px 8px;

  .commercialization-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--font-size-sm);
    color: var(--color-text-normal);
    cursor: pointer;

    input[type="checkbox"] {
      width: 16px;
      height: 16px;
    }
  }
}

.button {
  display: inline-block;
  margin: 15px auto;
  margin-bottom: 20px;
  padding: 15px 50px;
  border-radius: var(--radius-rounded);
  font-weight: var(--font-weight-bold);
  text-align: left;
  text-decoration: none;

  &.processing {
    background-color: var(--color-surface-muted);
    color: var(--color-inactive);
    cursor: wait;
  }

  @include mq(sp) {
    margin: 15px auto;
    padding: 10px 30px;
  }
}

.fish-info {
  background-color: var(--color-surface-base);
  padding: 20px;
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  position: relative;
  .fish-info-title {
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-low);
  }

  .fish-info-data {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .fish-info-data-title {
      font-size: var(--font-size-xxl);
      font-weight: var(--font-weight-bold);
      line-height: 2;
      color: var(--color-text-normal);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;

      .fish-info-data-title-icon {
        width: 34px;
        height: 34px;
      }
    }

    .fish-info-data-description {
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-bold);

      .fish-info-data-description-item {
        min-width: 6.25rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        .fish-category {
          font-size: var(--font-size-xs);
          color: var(--color-text-low);
          font-weight: var(--font-weight-regular);
        }

        .fish-value {
          font-size: var(--font-size-sm);
          color: var(--color-text-normal);
        }
      }
      .fish-info-data-description-item-plus {
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        color: var(--color-text-low);
        display: flex;
        align-items: center;
        justify-content: center;

        :deep(.material-design-icon) {
          width: 14px;
          height: 14px;
        }

        :deep(.material-design-icon__svg) {
          width: 14px;
          height: 14px;
          stroke-width: 2px;
          stroke: currentColor;
          fill: none;
        }
      }
    }
  }
  .fish-info-action {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
    .fish-info-action-buttons {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }
    .fish-purchase-button {
      border-radius: var(--radius-rounded);
      padding: 8px 14px;
      background-color: var(--color-primary);
      color: var(--color-text-on-fixed-bg);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-md);
      cursor: pointer;
      min-height: 40px;
      line-height: 1;

      :deep(.material-design-icon) {
        width: 18px;
        height: 18px;
      }

      :deep(.material-design-icon__svg) {
        width: 18px;
        height: 18px;
        stroke-width: 2px;
        stroke: currentColor;
        fill: none;
      }
    }
    .upgrade-button {
      border-radius: var(--radius-rounded);
      padding: 4px 10px;
      background-color: var(--color-brand-premium);
      color: var(--color-text-on-fixed-bg);
      font-weight: var(--font-weight-bold);
      font-size: var(--font-size-md);
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      min-height: 40px;
      line-height: 1;

      img {
        background-color: var(--color-text-on-fixed-bg);
        border-radius: var(--radius-circle);
        padding: 4px;
        width: 20px;
        height: 20px;
        box-sizing: content-box;
      }
    }
    .fish-info-links {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 20px;
    }
    a {
      font-size: var(--font-size-sm);
      color: var(--color-text-low);
      text-decoration: underline;
    }
  }

  .premium-badge {
    position: absolute;
    top: 15px;
    right: 15px;

    img {
      padding: 4px;
      width: 24px;
      height: 24px;
      box-sizing: content-box;
    }
  }
}

// 軽量スケルトンテキスト
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeleton-text {
  background-color: var(--color-surface-muted);
  border-radius: var(--radius-xs);
  position: relative;
  overflow: hidden;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
    animation: shimmer 1.5s infinite;
  }

  &.skeleton-amount {
    width: 120px;
    height: 48px;
    margin: 8px 0;
    vertical-align: middle;
  }

  &.skeleton-value {
    width: 60px;
    height: 18px;
    vertical-align: middle;
  }
}

// テーマ設定セクションのスタイル
.theme-settings-section {
  margin-top: 30px;
  margin-bottom: 30px;

  .trial-notice {
    margin-top: 8px;
    font-size: var(--font-size-xs);
    color: var(--color-text-low);
  }

  .theme-dropdown-container {
    margin-top: 15px;
    max-width: 300px;
  }

  .theme-dropdown {
    width: 100%;
    padding: 10px 12px;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
    background-color: var(--color-surface-muted);
    border: none;
    border-radius: var(--radius-xs);
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%23808080' d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    padding-right: 32px;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px var(--color-focus-ring);
    }

    option {
      font-weight: var(--font-weight-bold);
      padding: 8px;
    }
  }

  .language-label {
    margin-top: 20px;
    margin-bottom: 0;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
  }

  .language-dropdown-container {
    margin-top: 8px;
    max-width: 300px;
  }
}

// アシスタント猫セクションのスタイル
.assistant-cat-section {
  .h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 20px;
  }

  .section-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }

  .no-result-btn-wrapper {
    display: flex;
    gap: 1rem;
    margin-top: 15px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .assistant-cat-button {
    position: relative;
    display: inline-block;
    padding: 15px 50px;
    border-radius: var(--radius-rounded);
    font-weight: var(--font-weight-bold);
    text-align: left;
    text-decoration: none;
    background-color: var(--color-primary);
    color: var(--color-text-on-fixed-bg);
    border: none;
    cursor: pointer;

    @include hover-overlay;
  }

  .mode-title {
    margin: 10px 7px;
    display: flex;
    justify-content: space-between;
  }

  .btn-skip {
    background: var(--color-surface-muted);
    color: var(--color-text-normal);
  }

  .mode-change-button {
    border: none;
    color: var(--color-text-normal);
    cursor: pointer;
    background: none;
  }
}

.email-notification-section {
  margin-bottom: 20px;

  .email-magazine-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-top: 15px;

    @include mq(sp) {
      flex-direction: column;
      gap: 16px;
    }
  }

  .email-magazine-content {
    flex: 1;
    min-width: 0;
  }

  .email-magazine-title {
    margin: 0 0 8px;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-bold);
    color: var(--color-text-normal);
  }

  .email-magazine-description {
    margin: 0 0 8px;
    color: var(--color-text-low);
    font-size: var(--font-size-sm);
    line-height: 1.6;
  }

  .email-magazine-list {
    margin: 0;
    padding-left: 1.2em;
    color: var(--color-text-low);
    font-size: var(--font-size-sm);
    line-height: 1.6;
  }

  .email-magazine-options-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
    flex-shrink: 0;

    @include mq(sp) {
      align-items: flex-start;
      width: 100%;
    }
  }

  .email-magazine-options {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    flex-shrink: 0;
    padding-top: 4px;

    &.disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }

  .email-magazine-option {
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: none;
    padding: 0;
    color: var(--color-text-normal);
    font-size: var(--font-size-sm);
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
      cursor: not-allowed;
    }

    &.is-disabled {
      opacity: 0.5;
    }
  }

  .email-magazine-japanese-notice {
    margin: 0;
    max-width: 280px;
    font-size: var(--font-size-xs);
    color: var(--color-text-low);
    line-height: 1.5;
    text-align: right;

    @include mq(sp) {
      max-width: none;
      text-align: left;
    }
  }
}
</style>
